import { useTranslation } from 'react-i18next';
import TrashIcon from '../../assets/icons/trash.svg?react';
import { IconButton } from 'qampo-ui-library';
import { Dropdown, DropdownOptions, InputFieldNumber } from '../UI';
import { Button } from '../UI/base/Button';
import type {
  FlexStaffCompetenceRequirement,
  MandatoryStaffCompetenceRequirement,
  StaffProfessionResponse
} from '../../api/generated';
import styles from './StaffCompetenceRequirementsEditor.module.css';

// Local draft rows carry a stable `_key` so React can track them across add/remove
// (the API requirement types have no id). Stripped before building the booking payload.
export interface MandatoryRequirementDraft extends MandatoryStaffCompetenceRequirement {
  _key: string;
}
export interface FlexRequirementDraft extends FlexStaffCompetenceRequirement {
  _key: string;
}

interface StaffCompetenceRequirementsEditorProps {
  professions: StaffProfessionResponse[];
  mandatory: MandatoryRequirementDraft[];
  flex: FlexRequirementDraft[];
  onMandatoryChange: (next: MandatoryRequirementDraft[]) => void;
  onFlexChange: (next: FlexRequirementDraft[]) => void;
}

// Each mandatory row is single-profession in this UI. The backend supports several
// interchangeable competences per row (any one qualifies) plus a distinct-competences flag,
// but a room rental only ever needs one covering competence, so neither is offered here.
const firstProfessionId = (req: MandatoryStaffCompetenceRequirement): string | undefined =>
  req.staffProfessionIds?.[0];

export const StaffCompetenceRequirementsEditor = ({
  professions,
  mandatory,
  flex,
  onMandatoryChange,
  onFlexChange
}: StaffCompetenceRequirementsEditorProps) => {
  const { t } = useTranslation();
  const hasProfessions = professions.length > 0;
  const professionById = new Map(professions.map((p) => [p.id, p]));
  const professionName = (id: string | undefined): string | undefined =>
    id ? (professionById.get(id)?.name ?? id) : undefined;

  const addMandatory = () => {
    onMandatoryChange([
      ...mandatory,
      {
        _key: crypto.randomUUID(),
        staffProfessionIds: [professions[0].id],
        count: 1,
        preProcessingMinutes: 0,
        postProcessingMinutes: 0
      }
    ]);
  };

  const updateMandatory = (index: number, patch: Partial<MandatoryStaffCompetenceRequirement>) => {
    onMandatoryChange(mandatory.map((req, i) => (i === index ? { ...req, ...patch } : req)));
  };

  const removeMandatory = (index: number) => {
    onMandatoryChange(mandatory.filter((_, i) => i !== index));
  };

  const addFlex = () => {
    onFlexChange([...flex, { _key: crypto.randomUUID(), staffProfessionId: professions[0].id, count: 1 }]);
  };

  const updateFlex = (index: number, patch: Partial<FlexStaffCompetenceRequirement>) => {
    onFlexChange(flex.map((req, i) => (i === index ? { ...req, ...patch } : req)));
  };

  const removeFlex = (index: number) => {
    onFlexChange(flex.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.editor}>
      <div className={styles.group}>
        <span className={styles.groupTitle}>{t('room-booking-mandatory-title')}</span>
        {mandatory.map((req, index) => (
          <div key={req._key} className={styles.mandatoryRow}>
            <Dropdown
              width={180}
              placeholder={t('room-booking-select-profession')}
              selectedOption={professionName(firstProfessionId(req))}
            >
              <DropdownOptions
                options={professions}
                selectedOption={professionById.get(firstProfessionId(req) ?? '') ?? null}
                getKey={(p) => p.id}
                displayOption={(p) => p.name}
                handleChange={(p) => updateMandatory(index, { staffProfessionIds: [p.id] })}
              />
            </Dropdown>
            <InputFieldNumber
              width="80px"
              value={req.count ?? 1}
              allowNegative={false}
              suffixLabel={t('room-booking-count-suffix')}
              onChange={(e) => updateMandatory(index, { count: Number(e.target.value) || 0 })}
            />
            <InputFieldNumber
              width="96px"
              value={req.preProcessingMinutes ?? 0}
              allowNegative={false}
              suffixLabel={t('room-booking-minutes-suffix')}
              onChange={(e) =>
                updateMandatory(index, { preProcessingMinutes: Number(e.target.value) || 0 })
              }
            />
            <InputFieldNumber
              width="96px"
              value={req.postProcessingMinutes ?? 0}
              allowNegative={false}
              suffixLabel={t('room-booking-minutes-suffix')}
              onChange={(e) =>
                updateMandatory(index, { postProcessingMinutes: Number(e.target.value) || 0 })
              }
            />
            <IconButton
              size="small"
              icon={<TrashIcon width={14} height={14} />}
              onClick={() => removeMandatory(index)}
            />
          </div>
        ))}
        <Button type="tertiary" disabled={!hasProfessions} onClick={addMandatory}>
          {t('room-booking-add-mandatory')}
        </Button>
      </div>

      <div className={styles.group}>
        <span className={styles.groupTitle}>{t('room-booking-flex-title')}</span>
        {flex.map((req, index) => (
          <div key={req._key} className={styles.flexRow}>
            <Dropdown
              width={180}
              placeholder={t('room-booking-select-profession')}
              selectedOption={professionName(req.staffProfessionId)}
            >
              <DropdownOptions
                options={professions}
                selectedOption={professionById.get(req.staffProfessionId ?? '') ?? null}
                getKey={(p) => p.id}
                displayOption={(p) => p.name}
                handleChange={(p) => updateFlex(index, { staffProfessionId: p.id })}
              />
            </Dropdown>
            <InputFieldNumber
              width="80px"
              value={req.count ?? 1}
              allowNegative={false}
              suffixLabel={t('room-booking-count-suffix')}
              onChange={(e) => updateFlex(index, { count: Number(e.target.value) || 0 })}
            />
            <IconButton
              size="small"
              icon={<TrashIcon width={14} height={14} />}
              onClick={() => removeFlex(index)}
            />
          </div>
        ))}
        <Button type="tertiary" disabled={!hasProfessions} onClick={addFlex}>
          {t('room-booking-add-flex')}
        </Button>
      </div>
    </div>
  );
};
